import { endpoints } from '../config/endpoints';
import { apiFetch } from './client';
import type { Workspace } from '../types/workspace.types';

export async function fetchWorkspaces(): Promise<Workspace[]> {
  const response = await apiFetch(endpoints.workspaces.list());

  if (!response.ok) {
    throw new Error(`Failed to fetch workspaces: ${response.statusText}`);
  }

  const result = await response.json();
  return result.data || [];
}

export async function createWorkspace(name: string, description?: string): Promise<Workspace> {
  const response = await apiFetch(endpoints.workspaces.create(), {
    method: 'POST',
    body: JSON.stringify({ name, description }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `Failed to create workspace: ${response.statusText}`);
  }

  const result = await response.json();
  return result.workspace;
}

// Module-level promise cache: deduplicates concurrent calls for the same workspace name.
// If two calls race for "Excel", the second one reuses the first's in-flight promise
// instead of creating a duplicate workspace.
const inflightRequests = new Map<string, Promise<Workspace>>();

/**
 * Find an existing workspace by name (case-insensitive) or create one.
 * Concurrent calls for the same name are deduplicated at the module level.
 */
export async function findOrCreateWorkspace(name: string, description?: string): Promise<Workspace> {
  const key = name.toLowerCase();

  // If there's already an in-flight request for this name, reuse it
  const inflight = inflightRequests.get(key);
  if (inflight) {
    return inflight;
  }

  const promise = (async () => {
    try {
      const workspaces = await fetchWorkspaces();
      const existing = workspaces.find(
        (w) => w.name.toLowerCase() === key
      );

      if (existing) {
        return existing;
      }

      return await createWorkspace(name, description);
    } finally {
      inflightRequests.delete(key);
    }
  })();

  inflightRequests.set(key, promise);
  return promise;
}
